import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo, faqSchema } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

interface WebhookEvent {
  type: string;
  blurb: string;
  payload: string;
}

const EVENTS: WebhookEvent[] = [
  {
    type: "round.opened",
    blurb: "Fires when /rgs/round/open succeeds — wallet has been debited and the round is pre-rolled. Use this for live exposure tracking.",
    payload: `{
  roundId:    string,
  sessionId:  string,
  operatorId: string,
  gameSlug:   string,
  betAmount:  number,
  currency:   "BTC" | "ETH" | "SOL" | "USDT" | "USDC" | "USD",
  ts:         number   // epoch ms
}`,
  },
  {
    type: "round.resolved",
    blurb: "Fires once the server finalizes the outcome (cashout or bust). Carries the player payout and the cashout multiplier the player took.",
    payload: `{
  roundId:           string,
  sessionId:         string,
  operatorId:        string,
  gameSlug:          string,
  outcome:           "win" | "bust",
  cashoutMultiplier: number,
  payout:            number,
  balance:           number,    // post-resolve wallet balance
  ts:                number
}`,
  },
  {
    type: "session.launched",
    blurb: "Fires on every successful /rgs/session/launch. Useful for audit trails and KYC pipeline triggers.",
    payload: `{
  sessionId:  string,
  operatorId: string,
  playerId:   string,
  currency:   "BTC" | "ETH" | "SOL" | "USDT" | "USDC" | "USD",
  ts:         number
}`,
  },
  {
    type: "settlement.recorded",
    blurb: "Fires when a settlement period closes (typically weekly). Carries GGR, our 10% fee, and the net payout owed to the operator.",
    payload: `{
  settlementId: string,
  operatorId:   string,
  periodStart:  number,   // epoch ms
  periodEnd:    number,
  ggr:          number,   // gross gaming revenue for the period
  fee:          number,   // VB fee (10% of GGR)
  payout:       number,   // ggr - fee, owed to operator
  currency:     "BTC" | "ETH" | "SOL" | "USDT" | "USDC" | "USD",
  ts:           number
}`,
  },
];

const FAQ_ITEMS = [
  {
    q: "How do I verify the X-VB-Signature header?",
    a: "Compute HMAC-SHA256 over the raw request body using your shared secret, hex-encode it, and prefix with 'sha256='. Compare against the X-VB-Signature header using a constant-time comparison.",
  },
  {
    q: "What is the retry policy?",
    a: "A single retry is attempted 5 seconds after a non-2xx response or a 10-second timeout. Two consecutive failures mark the delivery as failed; you can re-fetch from /admin/webhook-deliveries.",
  },
  {
    q: "Which events does Vault Breaker emit?",
    a: "Four event types: round.opened, round.resolved, session.launched, and settlement.recorded. Configure the subset you want via POST /admin/operators/:id/webhooks.",
  },
];

export function Webhooks() {
  return (
    <>
      <Seo
        title="Webhooks · Vault Breaker Studios"
        description="Asynchronous server-to-operator callback contract for Vault Breaker — events, payloads, HMAC signature verification, retry policy."
        path="/webhooks"
        jsonLd={faqSchema(FAQ_ITEMS)}
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          WEBHOOKS · AVAILABLE — V1
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Webhooks
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 720, lineHeight: 1.55 }}>
          Server-to-operator async callbacks for round lifecycle, session launch, and weekly
          settlement. Deliveries are HMAC-signed, retried once on failure, and queryable from
          the admin API. Wire up a handler, register the URL, and you're live.
        </p>
        <Card
          style={{
            marginTop: 16,
            padding: "12px 18px",
            background: "rgba(0,217,126,0.08)",
            border: "1px solid var(--green)",
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            maxWidth: 720,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: "var(--green)",
              marginTop: 6,
              flexShrink: 0,
            }}
          />
          <span style={{ fontSize: 13, color: "var(--fg-1)", lineHeight: 1.55 }}>
            <strong>Best practice:</strong> respond <code>200</code> within 1 second and queue
            the actual work async. We treat anything other than 2xx as a failure and a 10s
            timeout will exhaust your single retry.
          </span>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="Setup" title="Register an endpoint" />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            POST <code>/admin/operators/:id/webhooks</code> with a JSON body containing your
            inbound URL, the event subset you want, and a shared secret. The secret is stored
            once; reveal it again only by rotating.
          </p>
          <pre
            style={{
              margin: 0,
              padding: "14px 18px",
              background: "var(--bg-1)",
              border: "1px solid var(--line)",
              borderRadius: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-1)",
              overflow: "auto",
              whiteSpace: "pre",
              lineHeight: 1.6,
            }}
          >{`POST /admin/operators/acme/webhooks
content-type: application/json
authorization: Bearer <admin token>

{
  "url":     "https://wallet.acme.example/webhooks/vaultbreaker",
  "events":  ["round.resolved", "settlement.recorded"],
  "secret":  "whsec_…"
}`}</pre>
          <p style={{ margin: 0, fontSize: 13, color: "var(--fg-3)", lineHeight: 1.55 }}>
            You can also register/rotate via <Link to="/admin" style={{ color: "var(--cyan)" }}>/admin →
            Operators → Webhooks</Link>.
          </p>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="Delivery headers" title="What we send on every call" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <Row label="Content-Type" value="application/json" />
          <Row label="X-VB-Event" value="round.resolved (or one of the 4 event types)" />
          <Row label="X-VB-Delivery" value="evt_… (unique idempotency key per delivery attempt)" />
          <Row label="X-VB-Signature" value="sha256=<hex(HMAC-SHA256(secret, raw body))>" />
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="Events" title="What gets sent" />
        <div style={{ display: "grid", gap: 14 }}>
          {EVENTS.map((e) => (
            <Card key={e.type} style={{ padding: 0, overflow: "hidden" }}>
              <div
                style={{
                  padding: "14px 22px",
                  borderBottom: "1px solid var(--line)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <code style={{ fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--cyan)" }}>
                  {e.type}
                </code>
              </div>
              <div style={{ padding: "14px 22px", display: "grid", gap: 10 }}>
                <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
                  {e.blurb}
                </p>
                <pre
                  style={{
                    margin: 0,
                    padding: "10px 14px",
                    background: "var(--bg-1)",
                    border: "1px solid var(--line)",
                    borderRadius: 6,
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    color: "var(--fg-1)",
                    overflow: "auto",
                    whiteSpace: "pre",
                    lineHeight: 1.6,
                  }}
                >
                  {e.payload}
                </pre>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Verify" title="HMAC signature — JS" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <pre
            style={{
              margin: 0,
              padding: "18px 22px",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >{`// Node — Express middleware. Use the RAW body (no JSON.parse first).
import crypto from "node:crypto";

const secret = process.env.VB_WEBHOOK_SECRET; // e.g. "whsec_abcd1234"

export function verifyVbSignature(rawBody, headerSignature) {
  const expected =
    "sha256=" + crypto.createHmac("sha256", secret).update(rawBody).digest("hex");
  const a = Buffer.from(expected);
  const b = Buffer.from(headerSignature);
  return a.length === b.length && crypto.timingSafeEqual(a, b);
}

// Usage:
// app.post("/webhooks/vaultbreaker", express.raw({ type: "application/json" }), (req, res) => {
//   if (!verifyVbSignature(req.body, req.header("X-VB-Signature"))) return res.sendStatus(401);
//   res.sendStatus(200); // ack first, queue work async
// });`}</pre>
        </Card>
      </Section>

      <Section>
        <SectionHeading eyebrow="Verify" title="HMAC signature — Python" />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <pre
            style={{
              margin: 0,
              padding: "18px 22px",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-2)",
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
            }}
          >{`# Python — works in Flask / FastAPI / Django (use the RAW request body).
import hmac, hashlib, os

secret = os.environ["VB_WEBHOOK_SECRET"].encode()

def verify_vb_signature(raw_body: bytes, header_signature: str) -> bool:
    expected = "sha256=" + hmac.new(secret, raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, header_signature)`}</pre>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Reliability"
          title="Delivery semantics"
          lead="At-least-once, idempotent on X-VB-Delivery, single retry on failure."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          <Row label="Timeout per attempt" value="10 s — anything else is treated as a failure" />
          <Row label="Retry schedule" value="One retry, 5 s after the first failure" />
          <Row label="Failure threshold" value="Two consecutive failures (initial + retry) marks the delivery failed" />
          <Row label="Recovery" value="GET /admin/webhook-deliveries to re-fetch failed deliveries" />
          <Row label="Idempotency" value="Same X-VB-Delivery on retry — dedupe in your handler" />
        </Card>
      </Section>

      <Section>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/api-reference" style={{ textDecoration: "none" }}>
            <Button>API REFERENCE →</Button>
          </Link>
          <Link to="/security" style={{ textDecoration: "none" }}>
            <Button>SECURITY →</Button>
          </Link>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <Button variant="gold">CONTACT SALES</Button>
          </Link>
        </div>
      </Section>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        padding: "12px 22px",
        borderTop: "1px solid var(--line)",
        display: "grid",
        gridTemplateColumns: "240px 1fr",
        gap: 16,
      }}
    >
      <span className="label" style={{ fontSize: 11, color: "var(--fg-3)" }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: "var(--fg-2)", fontFamily: "var(--font-mono)" }}>
        {value}
      </span>
    </div>
  );
}
